import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArticleTopicSubscriberListing, rng, SiteView } from '../../../amorg-common';
import { getAllSites } from '../../../api/adminClientApi';
import { articlesClientApi } from '../../../api/articlesClientApi';
import { Button } from '../../../designSystem/button/Button';
import { colors } from '../../../designSystem/colors/color';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { ListRow } from '../../../designSystem/compositeComponents/ListRow';
import { Select } from '../../../designSystem/inputs/Select';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../designSystem/Message';
import { Modal } from '../../../designSystem/Modal';
import { Small } from '../../../designSystem/typography/Body';
import { H1, H2 } from '../../../designSystem/typography/Heading';
import { ArticlesTab } from './_components/ArticlesTab';
import { AmorgyPageWrapper } from '../../_pageWrappers/AmorgyPageWrapper';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';

export default function ArticlesSubscriberList (): JSX.Element 
{
    const [sites, setSites] = React.useState<SiteView[]>([]);
    const [subscribers, setSubscribers] = React.useState<ArticleTopicSubscriberListing[] | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);

    const [newSubscriberModal, setNewSubscriberModal] = React.useState<{ show: boolean, siteId: string; }>({ show: false, siteId: '' });

    const navigate = useNavigate();

    React.useEffect(() =>
    {
        document.title = 'Amorgy - Lovkrav abonnenter';
        const getData = async () =>
        {
            setSites(await getAllSites(() => navigate('/amorgy/login')));
            setSubscribers(await articlesClientApi.getSubscriberListings());
            setLoading(false);
        };
        getData();
    }, []);

    async function handleClickAddSubscriber ()
    {
        setLoading(true);
        await articlesClientApi.createSubscriber(rng.getRandomId(), newSubscriberModal.siteId);
        setSubscribers(await articlesClientApi.getSubscriberListings());
        setNewSubscriberModal({ show: false, siteId: '' });
        setLoading(false);
    }

    return (
        <AmorgyPageWrapper selectedTab='articles'
            subMenuContent={
                <ArticlesTab selectedTab='subscribers' />
            }>

            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout narrow>
                <Flex flexDirection='column' gap={16}>
                    {subscribers !== null &&
                        <>
                            <Flex justifyContent='space-between'>
                                <H1>Abonnenter</H1>
                                <Button icon="actions_plus" variant="filled" text="Opret abonnent" onClick={() => setNewSubscriberModal({ show: true, siteId: '' })} />
                            </Flex>


                            {subscribers.length === 0 &&
                                <MessageCard variant='info'>
                                    Ingen abonnenter oprettet
                                </MessageCard>
                            }

                            <div>
                                {subscribers.sort((a, b) => a.siteId.localeCompare(b.siteId)).map((s) =>
                                {
                                    const site = sites.find(site => site.id === s.siteId);
                                    if (!site)
                                        return null;

                                    return <SubscriberRow key={s.subscriberId} subscriber={s} site={site} />;
                                })}
                            </div>



                            <Modal title="Opret abonnent" showModal={newSubscriberModal.show} onCancel={() => setNewSubscriberModal({ show: false, siteId: '' })}
                                content={
                                    <Flex flexDirection='column' gap={16}>
                                        <Select
                                            emptyText='Vælg organisation'
                                            options={
                                                sites.filter(site =>
                                                    subscribers.every(sub =>
                                                        sub.siteId !== site.id
                                                    )
                                                ).map(site => ({ value: site.id, text: site.id }))
                                            }
                                            onChange={(value) => setNewSubscriberModal({ show: true, siteId: value })}
                                            selectedValue={newSubscriberModal.siteId}
                                        />
                                        <Button variant='filled' text='Tilføj' disabled={newSubscriberModal.siteId === ''} onClick={handleClickAddSubscriber} />
                                    </Flex>
                                }
                            />

                        </>
                    }
                </Flex>
            </SingleColumnLayout>
        </AmorgyPageWrapper>
    );
}

type SubscriberRowProps = {
    subscriber: ArticleTopicSubscriberListing;
    site: SiteView,
    expanded?: boolean,
};
function SubscriberRow (props: SubscriberRowProps): JSX.Element
{
    const navigate = useNavigate();

    const subscriptionCount = props.subscriber.lists.reduce((acc, l) => acc + l.topics.reduce((acc2, t) => acc2 + t.subscriptionCount, 0), 0);

    const subScriberName = props.subscriber.siteId;

    return (
        <ListRow
            onClick={() => navigate(`/amorgy/articles/subscribers/${props.subscriber.subscriberId}`)}
            styleVariant={'outlined'}
            stripColor={subscriptionCount == 0 ? colors.icon.passive : colors.icon.active}
            onCollapse={() => { return; }}
            collapsed={true}
            content={
                <Flex gap={8} justifyContent='space-between'>
                    <H2>{subScriberName} </H2>
                </Flex>
            }
            childRows={
                <>
                    {props.subscriber.lists.map((l) =>
                        <ArticleListRow key={l.listId}
                            subscriberId={props.subscriber.subscriberId}
                            list={l}
                        />
                    )}
                </>
            }
        />
    );
}

type ArticleListRowProps = {
    subscriberId: string;
    list: {
        listId: string,
        listName: string,
        topics: {
            topicId: string,
            topicName: string,
            articleCount: number,
            subscriptionCount: number;
        }[];
    };
};
function ArticleListRow (props: ArticleListRowProps): JSX.Element
{
    const navigate = useNavigate();

    const subscriptionCount = props.list.topics.reduce((acc, t) => acc + t.subscriptionCount, 0);
    return (
        <ListRow level={2}
            onClick={() => navigate(`/amorgy/articles/subscribers/${props.subscriberId}?lid=${props.list.listId}`)}
            styleVariant={props.list.topics.some(t => t.subscriptionCount > 0) ? 'outlined' : 'lightGray'}
            stripColor={props.list.topics.some(t => t.subscriptionCount > 0) ? colors.icon.active : colors.icon.passive}
            onCollapse={() => { return; }}
            content={
                <Flex justifyContent='space-between' alignItems='center'>
                    <Small>{props.list.listName}</Small>
                    <Flex gap={8} alignItems='center'>
                        {subscriptionCount > 0 &&
                            <Small>{subscriptionCount} lovkrav valgt</Small>
                        }
                    </Flex>
                </Flex>
            }
            childRows={
                <>
                    {props.list.topics.map(t =>
                        <TopicListRow key={t.topicId} topic={t} />
                    )}
                </>
            }
        />
    );
}

type TopicListRowProps = {
    topic: {
        topicId: string,
        topicName: string,
        articleCount: number,
        subscriptionCount: number;
    };
};
function TopicListRow (props: TopicListRowProps): JSX.Element
{
    return (
        <>
            <LoadingPageOverlay loading={false} />
            <ListRow
                level={2}
                styleVariant={props.topic.subscriptionCount > 0 ? 'outlined' : 'lightGray'}
                stripColor={props.topic.subscriptionCount > 0 ? colors.icon.active : colors.icon.passive}
                content={
                <Flex justifyContent='space-between' marginLeft={48} marginRight={48} alignItems='center'>
                    <Small>{props.topic.topicName}</Small>
                    {props.topic.subscriptionCount > 0 &&
                        <Small>{props.topic.subscriptionCount} lovkrav valgt</Small>
                    }
                </Flex>
            } />
        </>
    );
}